import React, { useEffect, useState } from 'react';
import { useLoading } from '../../contexts/LoadingContext';
import { httpClient } from '../../utils/apiCall';

/** Bu süreyi aşan işlemlerde kullanıcıya durumu açıklayan ek bir satır gösterilir. */
const UZUN_SUREN_ISLEM_ESIGI_MS = 10000;

interface LoadingPanelProps {
  forceVisible?: boolean;
  forcedMessage?: string;
}

const LoadingPanel: React.FC<LoadingPanelProps> = ({ forceVisible = false, forcedMessage }) => {
  const { isLoading, loadingMessage, resetLoading } = useLoading();
  const shouldShow = forceVisible || isLoading;
  const messageToDisplay = forcedMessage || loadingMessage;

  // İptal yalnızca veri okurken sunulur. Kaydetme/güncelleme/silme yarıda
  // kesilmez: sunucu isteği çoktan işlemiş olabilir.
  const canCancel = messageToDisplay.includes('GET') && httpClient.hasPendingReads();

  const handleCancel = () => {
    httpClient.cancelPendingReads();
    resetLoading();
  };

  // Uzun süren işlemlerde sessizce bekletmek yerine sebebini söyle; kullanıcı
  // İptal'e basıp basmayacağına bilerek karar versin.
  const [isTakingLong, setIsTakingLong] = useState(false);

  useEffect(() => {
    if (!shouldShow) {
      setIsTakingLong(false);
      return;
    }

    const timer = setTimeout(() => setIsTakingLong(true), UZUN_SUREN_ISLEM_ESIGI_MS);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  // Loading mesajını daha kullanıcı dostu hale getir
  const getDisplayMessage = (message: string) => {
    if (message.includes('GET')) return 'Veriler yükleniyor...';
    if (message.includes('POST')) return 'Kaydediliyor...';
    if (message.includes('PUT')) return 'Güncelleniyor...';
    if (message.includes('DELETE')) return 'Siliniyor...';
    if (message.includes('PATCH')) return 'Güncelleniyor...';
    return message;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full mx-4 transform transition-all duration-300 ease-in-out">
        {/* Loading Spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 dark:border-gray-700"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          </div>
        </div>
        
        {/* Loading Message */}
        <div className="text-center">
          <h3 className="text-gray-800 dark:text-gray-200 text-xl font-semibold mb-2">
            {getDisplayMessage(messageToDisplay)}
          </h3>
          
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
            {isTakingLong
              ? 'Sistem şu anda yoğun, bu işlem uzun sürebilir.'
              : 'Lütfen bekleyin...'}
          </p>
          
          {/* Progress Dots */}
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>

          {/* İptal: kullanıcı beklemek istemezse başka ekrana geçebilsin */}
          {canCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className="mt-6 px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 transition-colors"
            >
              İptal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingPanel;
